'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { Card, CardContent } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { X, Volume2, Sparkles } from 'lucide-react'
import { getDefaultTrialVoiceId, TRIAL_ROLE_DISPLAY_NAME } from '@/config/trial'
import { playAudioIOSOptimized } from '@/utils/audioPlayer'

interface VoiceRole {
  display_name: string
  description: string | null
  visibility_level: 'all' | 'vip'
  main_avatar_path: string | null
  original_name?: string
}

interface TrialRoleModalProps {
  onSelect: () => void
  onClose: () => void
}

// 角色名稱映射表（用於生成試聽音檔路徑）
const ROLE_NAME_MAP: Record<string, string> = {
  '水城 碧': 'mizuki_aoi',
  '水野 悠仁': 'mizuno_yūjin',
  '白石 美咲': 'shiraishi_misaki',
  '高木 颯真': 'takagi_souma',
  '高木 大和': 'takagi_yamato',
  '白石美穗': 'shiraishi_miho',
  '神谷蓮': 'jinya_ren',
}

// 提取角色名稱和假名（支援半角和全角括號）
const parseDisplayName = (displayName: string): { name: string; kana: string | null } => {
  if (!displayName || typeof displayName !== 'string') {
    return { name: displayName, kana: null }
  }

  // 處理半角括號
  let cleanName = displayName.split('(')[0].trim()
  let kana: string | null = null
  
  // 處理全角括號
  if (displayName.includes('（')) {
    cleanName = displayName.split('（')[0].trim()
    const kanaMatch = displayName.match(/（([^）]+)/)
    if (kanaMatch) {
      kana = kanaMatch[1]
    }
  } else if (displayName.includes('(')) {
    const kanaMatch = displayName.match(/\(([^)]+)/)
    if (kanaMatch) {
      kana = kanaMatch[1]
    }
  }

  return { name: cleanName, kana }
}

// 生成自我介紹音檔路徑
const getIntroAudioPath = (displayName: string): string | null => {
  if (!displayName || typeof displayName !== 'string') {
    return null
  }

  // 從 display_name 提取角色名稱（移除括號內的內容，支援半角和全角括號）
  const { name: cleanDisplayName } = parseDisplayName(displayName)
  const roleName = ROLE_NAME_MAP[cleanDisplayName]
  
  if (!roleName) {
    return null
  }

  // 試用版固定播放 01
  return `https://tts.jlpt-aitutor.com/audio-files/self-introduction/${roleName}/${roleName}01.wav`
}

export const TrialRoleModal: React.FC<TrialRoleModalProps> = ({ onSelect, onClose }) => {
  const [roles, setRoles] = useState<VoiceRole[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [playingRole, setPlayingRole] = useState<string | null>(null)
  const [trialRole, setTrialRole] = useState<VoiceRole | null>(null)
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch('/api/public/voice-roles')
        if (!response.ok) {
          throw new Error('Failed to fetch roles')
        }
        const data = await response.json()
        setRoles(data)

        // 找到試用角色（白石美穗）
        const trial = data.find((role: VoiceRole) => 
          role.display_name.includes(TRIAL_ROLE_DISPLAY_NAME)
        )
        if (trial) {
          setTrialRole(trial)
        }
      } catch (error) {
        // 載入角色失敗，使用空列表
      } finally {
        setIsLoading(false)
      }
    }

    fetchRoles()
  }, [])

  const handlePlaySample = async (role: VoiceRole) => {
    if (playingRole === role.display_name) return

    // 停止前一個正在播放的音訊
    if (currentAudioRef.current) {
      currentAudioRef.current.pause()
      currentAudioRef.current.currentTime = 0
      currentAudioRef.current = null
    }

    const audioUrl = getIntroAudioPath(role.display_name)
    if (!audioUrl) {
      return
    }

    setPlayingRole(role.display_name)
    
    // 創建新的 Audio 對象並追蹤
    const audio = new Audio(audioUrl)
    currentAudioRef.current = audio
    
    // 設置事件處理
    audio.addEventListener('ended', () => {
      setPlayingRole(null)
      currentAudioRef.current = null
    }, { once: true })
    
    audio.addEventListener('error', () => {
      setPlayingRole(null)
      currentAudioRef.current = null
    }, { once: true })
    
    try {
      await audio.play()
    } catch (error) {
      setPlayingRole(null)
      currentAudioRef.current = null
    }
  }

  // 組件卸載時清理音訊
  useEffect(() => {
    return () => {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause()
        currentAudioRef.current.currentTime = 0
        currentAudioRef.current = null
      }
    }
  }, [])

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-white">載入中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700">
        <CardContent className="p-6">
          {/* 標題和關閉按鈕 */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-yellow-400" />
              <h2 className="text-2xl font-bold text-white">選擇試用角色</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* 試用角色（白石美穗）- 大卡片 */}
          {trialRole && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-2 border-yellow-500 rounded-lg p-6">
                <div className="flex items-center gap-4 mb-4">
                  {trialRole.main_avatar_path && (
                    <div className="w-24 h-24 flex-shrink-0">
                      <Image
                        src={trialRole.main_avatar_path}
                        alt={trialRole.display_name}
                        width={96}
                        height={96}
                        className="w-full h-full object-contain rounded-lg select-none"
                        unoptimized
                        style={{ userSelect: 'none', WebkitUserSelect: 'none', pointerEvents: 'auto' }}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = '/images/default-avatars/default_female.png'
                        }}
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <span className="inline-block px-2 py-1 bg-yellow-500 text-black text-xs font-semibold rounded mb-1">
                      免費試用
                    </span>
                    <h3 className="text-xl font-bold text-white mb-1">
                      {parseDisplayName(trialRole.display_name).name}
                    </h3>
                    {parseDisplayName(trialRole.display_name).kana && (
                      <p className="text-xs text-gray-300 mb-1">
                        （{parseDisplayName(trialRole.display_name).kana}）
                      </p>
                    )}
                    {trialRole.original_name && (
                      <p className="text-xs text-gray-300 mb-2">
                        CV：{trialRole.original_name}
                      </p>
                    )}
                    {trialRole.description && (
                      <p className="text-sm text-gray-400">{trialRole.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => handlePlaySample(trialRole)}
                    variant="outline"
                    className="flex-1"
                    disabled={playingRole === trialRole.display_name}
                  >
                    <Volume2 className="h-4 w-4 mr-2" />
                    {playingRole === trialRole.display_name ? '播放中...' : '試聽語音'}
                  </Button>
                  <Button
                    onClick={onSelect}
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                  >
                    開始試用
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* 訂閱後可用角色 - 小卡片網格 */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              訂閱後可使用角色
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {roles
                .filter(role => !role.display_name.includes(TRIAL_ROLE_DISPLAY_NAME))
                .map((role) => (
                  <button
                    key={role.display_name}
                    onClick={() => handlePlaySample(role)}
                    disabled={playingRole === role.display_name}
                    className="relative group bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700 hover:border-gray-600"
                  >
                    {/* 角色頭像 */}
                    <div className="aspect-square mb-2">
                      <Image
                        src={role.main_avatar_path || '/images/default-avatars/default_role.png'}
                        alt={role.display_name}
                        width={200}
                        height={200}
                        className="w-full h-full object-contain rounded select-none"
                        unoptimized
                        style={{ userSelect: 'none', WebkitUserSelect: 'none', pointerEvents: 'auto' }}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = '/images/default-avatars/default_role.png'
                        }}
                      />
                    </div>
                    
                    {/* 角色名稱 */}
                    <div className="text-center mb-1">
                      <p className="text-sm font-medium text-white truncate">
                        {parseDisplayName(role.display_name).name}
                      </p>
                      {parseDisplayName(role.display_name).kana && (
                        <p className="text-xs text-gray-300 truncate">
                          （{parseDisplayName(role.display_name).kana}）
                        </p>
                      )}
                      {role.original_name && (
                        <p className="text-xs text-gray-300 truncate">
                          CV：{role.original_name}
                        </p>
                      )}
                    </div>
                    
                    {/* 權限標籤 */}
                    <div className="flex justify-center gap-1 mb-1">
                      {role.visibility_level === 'vip' && (
                        <span className="px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-semibold rounded">
                          VIP
                        </span>
                      )}
                      {role.visibility_level === 'all' && (
                        <span className="px-1.5 py-0.5 bg-gray-600 text-white text-xs font-semibold rounded">
                          普通
                        </span>
                      )}
                    </div>

                    {/* 播放狀態指示 */}
                    {playingRole === role.display_name && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                        <Volume2 className="h-6 w-6 text-white animate-pulse" />
                      </div>
                    )}
                  </button>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

